import { NEON_ENV_VAR_KEYS, createApiFromOptions, credentialEnvKeys, credentialName, fetchEnvKeys, policyEnvKeys, previewCredentialScopes, resolveBranchPolicy, toEntries } from "./env.js";
import { credentialScopesSatisfied } from "@neon/config/v1";
//#region src/reuse-secrets.ts
/**
* Resolve a branch's env while keeping one-time secrets the caller already holds.
*
* {@link fetchEnvKeys} — and the public `fetchEnv` — only ever *fetch*. The Neon API returns a
* credential's `api_token` / `s3_secret_access_key` exactly once, at mint time, so "fetching"
* them means minting a new credential; a plain `fetchEnv` on every `neon dev` start or `env
* pull` would leave a live credential behind each time. This is the wrapper that avoids that:
* it looks at what the caller already has, decides what is still usable, and asks `fetchEnv`
* for only the rest.
*
* The check is a real verification, not a presence test. A persisted secret is kept only when
* it names a credential that still exists on this branch, is not revoked or expired, and
* carries every scope the policy needs. A `.env.example` placeholder, a credential revoked in
* the console, one copied in from another branch, or one predating a newly-enabled feature all
* fail that check and get replaced.
*
* None of this needs local bookkeeping, because the secrets carry their own credential id:
* `AWS_ACCESS_KEY_ID` **is** the credential's `tokenId` (the storage gateway authenticates
* against the full id), and the AI Gateway token is minted as `nt_live_<tokenIdShort>_<secret>`,
* where `tokenIdShort` is what the credentials list reports. The env source being replaced is
* the record of what the last call issued.
*
* ```ts
* import { fetchEnvReusingSecrets } from "@neon-internals/env-core/reuse-secrets";
*
* const { vars, credential } = await fetchEnvReusingSecrets(config, {
*     projectId,
*     branch: "main",
*     env: { ...process.env, ...readEnvFile(".env") },
* });
* if (credential.issued) console.log(`new values for ${credential.keys.join(", ")}`);
* ```
*/
async function fetchEnvReusingSecrets(config, options) {
	const { env: source = process.env, keys: requestedKeys, revokeSuperseded = true, ...fetchOptions } = options;
	const api = options.api ?? createApiFromOptions(options);
	const { branch, desired } = await resolveBranchPolicy(config, options, api);
	const allPolicyKeys = policyEnvKeys(desired);
	const requested = requestedKeys ? new Set(requestedKeys) : null;
	const selectedPolicyKeys = requested === null ? allPolicyKeys : allPolicyKeys.filter((key) => requested.has(key));
	const selected = new Set(selectedPolicyKeys);
	const K = NEON_ENV_VAR_KEYS;
	const storageCredentialSelected = (desired.preview?.buckets.length ?? 0) > 0 && (selected.has(K.storage.accessKeyId) || selected.has(K.storage.secretAccessKey));
	const gatewayCredentialSelected = (desired.preview?.aiGatewayEnabled ?? false) && selected.has(K.aiGateway.apiKey);
	const secretKeys = credentialEnvKeys({
		storage: storageCredentialSelected,
		aiGateway: gatewayCredentialSelected
	}).filter((key) => selected.has(key));
	if (secretKeys.length === 0) {
		const fetched = await fetchEnvKeys(config, fetchOptions, requested === null ? null : selectedPolicyKeys);
		return {
			vars: preferPersisted(toEntries(fetched), source),
			credential: {
				issued: false,
				keys: [],
				revoked: [],
				superseded: []
			}
		};
	}
	const persisted = readPersistedSecrets(source);
	const storageCredentialManaged = requested === null || storageCredentialSelected;
	const gatewayCredentialManaged = requested === null || gatewayCredentialSelected;
	const complete = (!storageCredentialSelected || Boolean(persisted.accessKeyId && persisted.secretAccessKey)) && (!gatewayCredentialSelected || Boolean(persisted.apiToken));
	const named = storageCredentialManaged && persisted.accessKeyId !== "" || gatewayCredentialManaged && persisted.apiToken !== "" ? namedCredentials(await api.listCredentials(options.projectId, branch.id), persisted) : {
		storage: null,
		gateway: null
	};
	const reusable = complete ? reusableCredential(named, {
		storageEnabled: storageCredentialSelected,
		gatewayEnabled: gatewayCredentialSelected
	}) : null;
	const scopes = previewCredentialScopes(desired.preview, {
		storage: storageCredentialSelected,
		aiGateway: gatewayCredentialSelected
	});
	const keep = reusable !== null && credentialScopesSatisfied(reusable.scopes, scopes);
	const fetchKeys = keep ? selectedPolicyKeys.filter((key) => !secretKeys.includes(key)) : selectedPolicyKeys;
	const fetched = await fetchEnvKeys(config, {
		...fetchOptions,
		branchId: branch.id,
		api
	}, fetchKeys);
	const vars = preferPersisted(toEntries(fetched), source);
	if (keep) {
		for (const key of secretKeys) {
			const value = source[key];
			if (value !== void 0) vars[key] = value;
		}
		return {
			vars,
			credential: {
				issued: false,
				keys: secretKeys,
				revoked: [],
				superseded: []
			}
		};
	}
	const ours = /* @__PURE__ */ new Set();
	for (const meta of [storageCredentialManaged ? named.storage : null, gatewayCredentialManaged ? named.gateway : null]) if (meta !== null && meta.principalType === "user" && meta.name === credentialName(branch.name)) ours.add(meta.tokenId);
	if (revokeSuperseded) for (const tokenId of ours) await api.revokeCredential(options.projectId, branch.id, tokenId);
	return {
		vars,
		credential: {
			issued: true,
			keys: secretKeys,
			revoked: revokeSuperseded ? [...ours] : [],
			superseded: revokeSuperseded ? [] : [...ours]
		}
	};
}
/** Read the branch credential's secrets out of an env source. */
function readPersistedSecrets(source) {
	const storage = NEON_ENV_VAR_KEYS.storage;
	const gateway = NEON_ENV_VAR_KEYS.aiGateway;
	return {
		accessKeyId: source[storage.accessKeyId] ?? "",
		secretAccessKey: source[storage.secretAccessKey] ?? "",
		apiToken: source[gateway.apiKey] ?? ""
	};
}
/**
* Keep a persisted value rather than overwriting it with an empty fetched one.
*
* Neon Auth's `base_url` is the case that needs this: integrations created before the API
* returned it answer with an empty string, and the persisted copy is the only one left. An
* empty fetched value never carries more information than a non-empty persisted one, so
* preferring the latter is safe for every var — and it keeps a pull from blanking a working
* line in someone's `.env`.
*/
function preferPersisted(vars, source) {
	const out = { ...vars };
	for (const [key, value] of Object.entries(out)) {
		if (value !== "") continue;
		const persisted = source[key];
		if (persisted !== void 0 && persisted !== "") out[key] = persisted;
	}
	return out;
}
/**
* The credential id embedded in an AI Gateway token. The API mints them as
* `nt_live_<tokenIdShort>_<secret>`, and `tokenIdShort` is the public identifier the credentials
* list reports — so a persisted token names the credential that issued it. Returns `null` for
* anything not in that shape (a `.env.example` placeholder, a hand-typed value), which callers
* treat as unverifiable.
*/
function gatewayTokenIdShort(apiToken) {
	return /^nt_live_([^_]+)_.+$/.exec(apiToken)?.[1] ?? null;
}
/** Whether an issued credential can still be used: not revoked, not past its expiry. */
function isLiveCredential(meta, now) {
	if (meta.revokedAt !== void 0) return false;
	if (meta.expiresAt === void 0) return true;
	const expiresAt = Date.parse(meta.expiresAt);
	return Number.isNaN(expiresAt) || expiresAt > now;
}
/**
* The live credentials the persisted secrets name — at most one per half. A half that names
* nothing contributes nothing, which is what a placeholder, a credential revoked in the
* console, and one copied in from another branch all look like from here.
*/
function namedCredentials(live, persisted) {
	const usable = live.filter((meta) => isLiveCredential(meta, Date.now()));
	const shortId = persisted.apiToken ? gatewayTokenIdShort(persisted.apiToken) : null;
	return {
		storage: persisted.accessKeyId ? usable.find((meta) => meta.tokenId === persisted.accessKeyId) ?? null : null,
		gateway: shortId ? usable.find((meta) => meta.tokenIdShort === shortId) ?? null : null
	};
}
/**
* The credential the persisted secrets can be *reused* as, or `null`.
*
* Strict on purpose: every half the policy enables has to name a live credential, and when both
* features are enabled they must name the *same* one — they share a single credential, so
* halves that disagree came from two different calls and neither can be trusted.
*/
function reusableCredential(named, enabled) {
	if (enabled.storageEnabled && enabled.gatewayEnabled) return named.storage && named.gateway && named.storage.tokenId === named.gateway.tokenId ? named.storage : null;
	if (enabled.storageEnabled) return named.storage;
	if (enabled.gatewayEnabled) return named.gateway;
	return null;
}
//#endregion
export { fetchEnvReusingSecrets };

//# sourceMappingURL=reuse-secrets.js.map